/**
 * Quick task search — spec 01 §8's "Quick search task (từ task cache sync; badge khi
 * kết quả có thể cũ)".
 *
 * ── Two staleness facts, two places on screen, never merged ─────────────────
 *
 * `openapi.yaml` is explicit that merging them "would leave a caller unable to tell
 * 'achau is down' from 'the sweep is late' — two situations needing different
 * actions from different people". So:
 *
 *   `degraded`      the answer could not be confirmed against achau during this
 *                   request — a failed fallback call, or an open breaker. Positive
 *                   evidence, not a suspicion: the breaker opens only after five
 *                   consecutive availability failures. Rendered as `DataDelayed`
 *                   `source='achau'` on the **region**.
 *   `cache_status`  the state of this company's `sync_tasks` sweep — `fresh`,
 *                   `stale`, or `never_synced`, which is an operator's problem and
 *                   not an empty task list. Also region-level, and it says which of
 *                   the three it is rather than "data may be old".
 *   `stale`         per **row**: this row's `synced_at` is older than twice
 *                   `WP_TASK_SYNC_INTERVAL`. Always `false` on a row read live from
 *                   achau. Rendered as a badge on the row it is about, because a
 *                   response-level badge would libel the fresh rows beside it.
 *
 * ── `closed` is a badge, not a disabled button ──────────────────────────────
 *
 * Spec 01 §8: a closed task refuses a time entry *unless the caller holds a special
 * permission*. The client does not know whether this caller holds it — that is step
 * 3 of the 02 §11 chain and it is answered on the server — so the button stays
 * enabled and the server's refusal is rendered. Greying it would make a permission
 * somebody actually holds look like a broken control.
 */

import { useState, type ReactNode } from 'react';

import { useI18n, type Translate } from '../../i18n';
import { formatInstantWithAge } from '../../i18n/datetime';
import { taskSearchPath } from '../../api/paths';
import type { TaskSearchResponse, TaskSearchResultItem } from '../../api/types';
import { useApiQuery } from '../../api/useApiQuery';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { Badge } from '../../states/Badge';
import { DataDelayed } from '../../states/DataDelayed';
import { EmptyState } from '../../states/EmptyState';
import { Skeleton } from '../../states/Skeleton';
import { useTimer } from '../../timer/TimerProvider';

/** Below this the cache read is noise rather than a search. */
const MIN_QUERY_LENGTH = 2;

function RowBadges({
  item,
  t,
  locale,
  timezone,
}: {
  readonly item: TaskSearchResultItem;
  readonly t: Translate;
  readonly locale: string;
  readonly timezone: string;
}): ReactNode {
  return (
    <span className="wp-flags">
      {item.closed === true ? (
        <Badge
          severity="medium"
          icon="ban"
          label={t('tasks.closed')}
          title={t('tasks.closed.help')}
          testId={`task-closed-${item.task_id}`}
        />
      ) : null}
      {item.stale ? (
        <Badge
          severity="info"
          icon="hourglass"
          label={t('tasks.stale')}
          title={
            item.cached_at === null || item.cached_at === undefined
              ? t('tasks.stale.help')
              : t('tasks.stale.since', {
                  when: formatInstantWithAge(item.cached_at, locale, timezone, t('common.never')),
                })
          }
          testId={`task-stale-${item.task_id}`}
        />
      ) : null}
      <Badge
        severity="info"
        icon="circle-check"
        label={item.source === 'achau' ? t('tasks.source.achau') : t('tasks.source.cache')}
        testId={`task-source-${item.task_id}`}
      />
    </span>
  );
}

export function TaskSearch({ timezone }: { readonly timezone: string }): ReactNode {
  const { t, locale } = useI18n();
  const [query, setQuery] = useState('');
  const { start, busy, entry } = useTimer();

  const trimmed = query.trim();
  const path = trimmed.length >= MIN_QUERY_LENGTH ? taskSearchPath(trimmed) : null;
  const { status, data, error, reload } = useApiQuery<TaskSearchResponse>(path);

  const newestCachedAt =
    data === null
      ? null
      : (data.data
          .map((row) => row.cached_at ?? null)
          .filter((value): value is string => value !== null)
          .sort()
          .at(-1) ?? null);

  return (
    <section className="wp-section" aria-labelledby="task-search-heading">
      <h2 className="wp-section__title" id="task-search-heading">
        {t('tasks.heading')}
      </h2>

      <div className="wp-field">
        <label htmlFor="task-search-input">{t('tasks.searchLabel')}</label>
        <input
          id="task-search-input"
          type="search"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
          data-testid="task-search-input"
        />
        <p className="wp-small wp-muted">{t('tasks.searchHint', { min: MIN_QUERY_LENGTH })}</p>
      </div>

      {path === null ? null : status === 'loading' ? (
        <Skeleton rows={3} label={t('tasks.loading')} testId="task-search-skeleton" />
      ) : null}

      {status === 'error' && error !== null ? (
        <ApiErrorNote
          error={error}
          onRetry={reload}
          subject={t('tasks.subject')}
          testId="task-search-error"
        />
      ) : null}

      {data === null ? null : (
        <>
          {data.degraded ? (
            <DataDelayed
              source="achau"
              since={newestCachedAt}
              timezone={timezone}
              detail={t('tasks.degraded.detail')}
              testId="task-search-degraded"
            />
          ) : null}

          {data.cache_status === 'fresh' ? null : (
            <DataDelayed
              source="achau"
              since={newestCachedAt}
              timezone={timezone}
              detail={
                data.cache_status === 'never_synced'
                  ? t('tasks.cache.neverSynced')
                  : t('tasks.cache.stale')
              }
              testId="task-search-cache"
            />
          )}

          {data.data.length === 0 ? (
            <EmptyState
              title={t('tasks.empty.title')}
              description={t('tasks.empty.body')}
              testId="task-search-empty"
            />
          ) : (
            <ul className="wp-rows" data-testid="task-search-results">
              {data.data.map((item) => (
                <li key={item.task_id} className="wp-row">
                  <span className="wp-row__main">
                    <span className="wp-mono">{item.task_id}</span> {item.title}
                    {item.project === null || item.project === undefined ? null : (
                      <span className="wp-small wp-muted"> · {item.project}</span>
                    )}
                  </span>
                  <RowBadges item={item} t={t} locale={locale} timezone={timezone} />
                  <button
                    type="button"
                    className="wp-button"
                    disabled={busy}
                    onClick={() => {
                      void start({ taskId: item.task_id, taskTitle: item.title });
                    }}
                    data-testid={`task-start-${item.task_id}`}
                  >
                    {entry === null ? t('tasks.start') : t('tasks.switch')}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
